import {
    Autocomplete,
    AutocompleteOwnerState,
    AutocompleteProps,
    AutocompleteRenderGetTagProps,
    Box,
    debounce,
    FormGroup,
    Popper,
    PopperProps,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { C1IconButton } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import {
    defaultIsEqual,
    defaultRenderLabel,
    defaultRenderOption,
    defaultRenderTags,
    Searchable,
} from 'components/core/autocomplete'
import { C1AutocompleteStyles } from 'components/core/autocomplete/styles'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { useSimpleModal } from 'lib/modals'
import { AllowedRefs } from 'lib/websocket-notify'
import { Filter, Search } from 'lucide-react'
import React, { FC, HTMLAttributes, ReactElement, ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ListHookFlags } from 'redux_toolkit/lib/createPagedListApi'
import { ReduxListCache } from 'redux_toolkit/lib/reducer'
import { AutocompleteListboxProps, Requestable, NonRequestable } from './C1AutocompleteSingleAsync'

const AutocompletePopper = (props: PopperProps & { 'aria-label'?: string }): React.ReactElement => (
    <Popper {...props} role="region" />
)

export interface SelectionsModalProps<T, Request> extends ClosableProps {
    currentSelections: T[]
    onSelectionChange: (selections: T[]) => void
    initialRequest: Request
}

export type LazyHookLike<Req, ResponseType> = () => {
    trigger: (args: Req, opts?: ListHookFlags) => { getPage?: (n: number) => void, request?: Promise<unknown> }
} & ReduxListCache<Array<ResponseType>>

type C1AutocompleteMultipleAsyncHookProps<T extends Searchable, Request extends NonRequestable | Requestable<AllowedRefs>> = {
    Req: { create: (q: Partial<Request>) => Request }
    reqExpandMask?: { paths: string[] }
    hook: LazyHookLike<Request, T>
    onSelected: (v: Array<T>, reason?: 'reset' | 'clear' | 'selected') => void
    getDisplayName?: (option: T | null) => string
    renderLabel?: (option: T) => string
    renderTags?: (
        value: T[],
        getTagProps: AutocompleteRenderGetTagProps,
        ownerState: AutocompleteOwnerState<T, true, undefined, undefined, 'div'>
    ) => React.ReactNode
    isEqual?: (option: T, value: T) => boolean
    helperText?: string
    autoFocus?: boolean
    label?: string | false
    placeholder?: string
    loading?: boolean
    error?: boolean
    initialRefs?: Request extends Requestable<infer R> ? R extends AllowedRefs ? R[] : never : never
    initialValue?: T[]
    value?: Array<T> | null
    ListboxComponent?: React.ComponentType<AutocompleteListboxProps>
    listHookFlags?: ListHookFlags
    SelectionsModal?: FC<SelectionsModalProps<T, Request>>
    /** IDs of values that cannot be removed (e.g., via backspace) */
    lockedValues?: string[]
} & Partial<AutocompleteProps<T, true, undefined, undefined>>

const componentProps = {
    paper: {
        sx: {
            minWidth: '300px',
        },
    },
    ListboxProps: {
        sx: {
            '& .MuiAutocomplete-groupLabel': {
                backgroundColor: 'background.default',
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
                py: 1,
            },
        },
    },
}
/* Uncontrolled autocompleting multi-selection input */
export const C1AutocompleteMultipleAsyncHook = <Request extends NonRequestable | Requestable<AllowedRefs>, T extends Searchable>({
    hook: useLazyHook,
    Req,
    reqExpandMask,
    initialValue,
    initialRefs,
    onSelected,
    isEqual = defaultIsEqual,
    renderLabel = defaultRenderLabel,
    renderOption = defaultRenderOption,
    renderTags = defaultRenderTags,
    value,
    disabled: _disabled,
    ListboxComponent,
    listHookFlags,
    SelectionsModal,
    lockedValues,
    ...rest
}: C1AutocompleteMultipleAsyncHookProps<T, Request>): React.ReactElement => {
    const {
        helperText,
        autoFocus,
        label,
        placeholder,
        loading: parentIsLoading,
        error: parentIsError,
        ...autocompleteProps
    } = rest
    const intl = useIntl()
    const inputId = useId()
    const helperTextId = useId()
    const liveRegionId = useId()
    const [_values, setValues] = useState<T[]>(initialValue || [])
    const v = value === undefined ? _values : value
    const [inputValue, setInputValue] = useState<string | null>(null)
    const [query, setQuery] = useState<string | null>(null)
    const onQueryChangeDebounced = React.useMemo(() => debounce(setQuery, 400), [setQuery])
    const { open: openModal } = useSimpleModal()
    const [isModalOpen, setIsModalOpen] = useState(false)

    if (process.env.NODE_ENV === 'development') {
        const wasControlled = useRef<boolean>(value !== undefined) // eslint-disable-line react-hooks/rules-of-hooks
        const isControlled = value !== undefined
        if (isControlled !== wasControlled.current) {
            throw new Error(`Illegal: don't change a controlled component to uncontrolled or vice versa: ${wasControlled.current} -> ${isControlled}`)
        }
        if ((initialRefs || initialValue) && value !== undefined) {
            throw new Error(`Illegal: don't mix initial values/refs && values ${initialValue}/${initialRefs} && ${value}`)
        }
    }

    const previousValue = useRef(value)
    // Hidden API: setting the value to [] is a hard reset
    // Note: we consider emtpy arrays to be equal because they are made inline and that causes loops
    const hardReset = value?.length === 0 && previousValue.current !== value && !previousValue.current?.length
    previousValue.current = value

    useEffect(() => {
        if (!hardReset) {
            return
        }
        setInputValue('')
        setQuery('')
        onSelected([], 'reset')
        // eslint-disable-next-line react-hooks/exhaustive-deps -- don't trigger on onSelected
    }, [hardReset])

    const filterOptions = useCallback(
        (existingList: T[], newList: ReadonlyArray<T>): T[] => {
            return [...existingList.filter((o) => !newList.some((r) => isEqual(o, r))), ...newList]
        },
        [isEqual],
    )

    // MUI requires that the value must be in the options list
    const [options, setOptions] = useState<T[]>(() =>
        (initialValue && initialValue.length > 0 ? filterOptions([], initialValue) : []))

    useEffect(() => {
        if (!v) {
            return
        }
        setOptions((o) => filterOptions(v, o))
    }, [v, setOptions, filterOptions])

    const { trigger, data, isError: apiError, isLoading, error, canLoadMore } = useLazyHook()
    const loading = parentIsLoading || isLoading

    useEffect(
        () => void (query !== null && !_disabled && trigger(Req.create({ query } as Partial<Request>), listHookFlags)),
        [query, _disabled, Req, listHookFlags], // eslint-disable-line react-hooks/exhaustive-deps
    )
    useEffect(() => void (data?.length && setOptions(filterOptions(v, data.flat() as ReadonlyArray<T>))), [data]) // eslint-disable-line react-hooks/exhaustive-deps

    // One shot initial value.  This is not a controlled input!
    const setInitialValue = useRef(false)
    useEffect(() => {
        if (!initialValue?.length || parentIsLoading || setInitialValue.current) {
            return
        }
        setInitialValue.current = true
        setOptions(filterOptions(options, initialValue))
        setValues(initialValue)
    }, [parentIsLoading, initialValue]) // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch initial values from refs
    // 0 -> initial, 1 -> triggered, 2 -> complete
    const initialRefsStateMachine = useRef(0)
    useEffect(() => {
        if (!initialRefs?.length || parentIsLoading || initialRefsStateMachine.current === 2 || initialValue?.length) {
            return
        }

        if (initialRefsStateMachine.current === 0) {
            const arg = { expandMask: reqExpandMask, refs: initialRefs } as Request
            const req = Req.create(arg)
            const { request: didStuff } = trigger(req, listHookFlags)
            if (didStuff) {
                initialRefsStateMachine.current = 1
            }
            return
        }

        if (!data?.length) {
            return
        }

        initialRefsStateMachine.current = 2
        const flatData = data.flat() as T[]
        setOptions(filterOptions(options, flatData))
        setValues(flatData)
    }, [parentIsLoading, initialRefs, data, trigger, Req]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleModalOpen = useCallback(() => {
        if (!SelectionsModal) return

        // Create initial request for the modal
        const initialRequest = Req.create({ query: query || '' } as Partial<Request>)

        setIsModalOpen(true)
        openModal(SelectionsModal, {
            currentSelections: v,
            onSelectionChange: (selections: T[]): void => {
                setValues(selections)
                onSelected(selections, 'selected')
                setIsModalOpen(false)
            },
            initialRequest,
        })()
    }, [SelectionsModal, openModal, v, onSelected, Req, query])

    const ListboxWithFooter = useMemo(() =>
        React.forwardRef<HTMLUListElement, HTMLAttributes<HTMLUListElement>>(
            (props, ref) => <ul ref={ref} {...props}>
                {props.children}
                {canLoadMore && <AutocompleteFooterMessage />}
            </ul>,
        ), [canLoadMore])

    if (apiError) {
        return (
            <>
                <TextField
                    size="small"
                    error
                    disabled
                    name="autocomplete"
                    id={inputId}
                    placeholder={placeholder}
                    sx={{ mb: 1 }}
                    aria-invalid="true"
                />
                <Box role="alert">
                    <APIError
                        err={error}
                        title={
                            <FormattedMessage
                                id="component_c1_autocomplete_search_error"
                                defaultMessage="Error: Failed to load options"
                            />
                        }
                    />
                </Box>
            </>
        )
    }

    const disabled = _disabled || (parentIsLoading && !setInitialValue.current)

    return (
        <Box sx={{ position: 'relative' }}>
            {/* Live region for screen reader announcements */}
            <Box
                component="span"
                id={liveRegionId}
                sx={visuallyHidden}
                role="status"
                aria-live="polite"
                aria-atomic="true"
            >
                {loading
                    ? intl.formatMessage({ id: 'component_c1_autocomplete_loading', defaultMessage: 'Loading options' })
                    : intl.formatMessage(
                        { id: 'component_c1_autocomplete_options_count', defaultMessage: '{count} options available' },
                        { count: options.length },
                    )}
            </Box>
            <Autocomplete<T, true, undefined, undefined>
                componentsProps={componentProps}
                multiple
                fullWidth
                disabled={disabled}
                loading={loading}
                clearIcon={false}
                filterOptions={(x): T[] => x}
                filterSelectedOptions
                value={v}
                options={options}
                getOptionLabel={renderLabel}
                isOptionEqualToValue={isEqual}
                renderOption={renderOption}
                inputValue={inputValue || ''}
                noOptionsText={
                    loading
                        ? rest.loadingText || '...'
                        : inputValue
                            ? intl.formatMessage(
                                {
                                    id: 'autocomplete_no_options_for_search_query_text',
                                    defaultMessage: '(No options for "{search_query}")',
                                },
                                { search_query: inputValue },
                            )
                            : intl.formatMessage(
                                {
                                    id: 'autocomplete_no_options',
                                    defaultMessage: '(No options)',
                                },
                            )
                }
                renderInput={(params): ReactElement => (<FormGroup>
                    <C1FormLabel label={label} htmlFor={inputId} disabled={disabled} />
                    <TextField
                        name="autocomplete"
                        {...params}
                        id={inputId}
                        sx={{
                            ...C1AutocompleteStyles.textField,
                            '& .MuiOutlinedInput-root': {
                                '&:focus-within': {
                                    outline: '2px solid',
                                    outlineColor: 'primary.main',
                                    outlineOffset: '2px',
                                },
                            },
                        }}
                        autoFocus={autoFocus}
                        fullWidth
                        error={parentIsError}
                        helperText={helperText}
                        placeholder={placeholder}
                        size="small"
                        InputProps={{
                            ...params.InputProps,
                            type: 'text',
                            'aria-busy': loading,
                            'aria-invalid': parentIsError,
                            'aria-describedby': helperText ? helperTextId : undefined,
                            endAdornment: <>
                                {SelectionsModal && !disabled && (
                                    <Stack
                                        sx={{
                                            position: 'absolute',
                                            gap: 1,
                                            alignItems: 'center',
                                            flexDirection: 'row',
                                            right: 27,
                                        }}
                                    >
                                        {loading && <C1CircularProgress color="inherit" />}
                                        <Tooltip title={<FormattedMessage id="component_c1_autocomplete_advanced_search" defaultMessage="Show filters" />}>
                                            <C1IconButton
                                                size="small"
                                                onClick={handleModalOpen}
                                                sx={{
                                                    mr: 1,
                                                    '&:focus-visible': {
                                                        outline: '2px solid',
                                                        outlineColor: 'primary.main',
                                                        outlineOffset: '2px',
                                                    },
                                                }}
                                                aria-label={intl.formatMessage({ id: 'component_c1_autocomplete_advanced_search', defaultMessage: 'Show filters' })}
                                                aria-expanded={isModalOpen}
                                            >
                                                <C1Icon icon={Filter} />
                                            </C1IconButton>
                                        </Tooltip>
                                    </Stack>
                                )}
                                {params.InputProps.endAdornment}
                            </>,
                        }}
                        FormHelperTextProps={{
                            id: helperTextId,
                        }}
                    />
                </FormGroup>)}
                renderTags={renderTags}
                onBlur={(): void => onQueryChangeDebounced.clear()}
                onOpen={(): void => void (query === null && setQuery(''))}
                onInputChange={(_event, newInputValue, reason): void => {
                    if (reason === 'reset' && options.length) {
                        // leave existing input and terms up....
                        return
                    }
                    setInputValue(newInputValue)
                    onQueryChangeDebounced(newInputValue)
                }}
                onChange={(_event, val): void => {
                    let newVal = val || []
                    // Ensure locked values cannot be removed (e.g., via backspace)
                    if (lockedValues?.length) {
                        const removedLockedItems = v.filter(
                            (item) => lockedValues.includes(item.id) && !newVal.some((nv) => isEqual(nv, item)),
                        )
                        if (removedLockedItems.length > 0) {
                            // Re-add locked items that were removed
                            newVal = [...removedLockedItems, ...newVal]
                        }
                    }
                    setValues(newVal)
                    onSelected(newVal, 'selected')
                    setInputValue(null)
                    onQueryChangeDebounced.clear()
                    setQuery('')
                }}
                renderGroup={(params): ReactNode => (
                    <Box key={params.key}>
                        <Box px={3} py={2}>
                            <Typography color="text.secondary">
                                {params.group}
                            </Typography>
                        </Box>
                        {params.children}
                    </Box>
                )}
                ListboxComponent={ListboxComponent || ListboxWithFooter}
                ListboxProps={{
                    canLoadMore,
                    autocompleteFooterMessage: <AutocompleteFooterMessage />,
                } as AutocompleteListboxProps}
                PopperComponent={(props): React.ReactElement => (
                    <AutocompletePopper
                        {...props}
                        aria-label={intl.formatMessage({ id: 'autocomplete_search_results', defaultMessage: 'Search results' })}
                    />
                )}
                {...autocompleteProps}
            />
        </Box>
    )
}

export const AutocompleteFooterMessage: FC = () => (
    <Stack p={2} pt={3} gap={1} alignItems="center" justifyContent="center" direction="row">
        <C1Icon icon={Search} sx={{ color: 'text.secondary' }} />
        <Typography color="text.secondary">
            <FormattedMessage
                id="component_c1_autocomplete_search_more_items"
                defaultMessage="Narrow your search to find more items"
            />
        </Typography>
    </Stack>
)
